import { useState, useEffect, useCallback, memo } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  Popper,
  Fade,
  ClickAwayListener,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import MenuIcon from '@mui/icons-material/Menu';
import { usePostHog } from '../widgets/PostHogProvider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import FamilyRestroomOutlinedIcon from '@mui/icons-material/FamilyRestroomOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';

interface NavChild {
  label: string;
  href: string;
  description?: string;
  icon?: React.ReactNode;
}

interface NavItem {
  label: string;
  href?: string;
  children?: NavChild[];
}

const navItems: NavItem[] = [
  {
    label: 'What we do',
    children: [
      {
        label: 'For Professionals',
        href: '/solutions/professionals',
        description: 'Tools for clinicians, therapists, and care teams',
        icon: <MedicalServicesOutlinedIcon />,
      },
      {
        label: 'For Clients & Caregivers',
        href: '/solutions/clients',
        description: 'Support for individuals and their families',
        icon: <FamilyRestroomOutlinedIcon />,
      },
      {
        label: 'Consulting',
        href: '/solutions/consulting',
        description: 'Technologist-in-the-loop implementation services',
        icon: <SupportAgentIcon />,
      },
    ],
  },
  { label: 'About us', href: '/about' },
  {
    label: 'Resources',
    children: [
      {
        label: 'All Resources',
        href: '/resources',
        description: 'Browse all guides, research, and stories',
        icon: <CollectionsBookmarkOutlinedIcon />,
      },
      {
        label: 'Research & Evidence',
        href: '/resources/research',
        description: 'Clinical evidence underpinning our approach',
        icon: <ScienceOutlinedIcon />,
      },
      {
        label: 'Trust',
        href: '/trust',
        description: 'Our compliance posture and public legal stack',
        icon: <VerifiedUserOutlinedIcon />,
      },
    ],
  },
  { label: 'Trust', href: '/trust' },
  { label: 'Pricing', href: '/pricing' },
];

function HeaderComponent() {
  const theme = useTheme();
  const { capture } = usePostHog();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Hide/show Chatwoot when drawer opens/closes
  useEffect(() => {
    window.$chatwoot?.toggleBubbleVisibility(mobileOpen ? 'hide' : 'show');
  }, [mobileOpen]);

  const handleMenuToggle = useCallback((event: React.MouseEvent<HTMLElement>, label: string) => {
    setOpenMenu((prev) => {
      if (prev === label) {
        setAnchorEl(null);
        return null;
      }
      setAnchorEl(event.currentTarget);
      return label;
    });
  }, []);

  const handleClickAway = useCallback((event: MouseEvent | TouchEvent) => {
    // Don't close if clicking on another dropdown trigger button
    const target = event.target as HTMLElement;
    if (target.closest('[data-dropdown-trigger]')) {
      return;
    }
    setOpenMenu(null);
    setAnchorEl(null);
  }, []);

  const handleMenuClose = useCallback(() => {
    setOpenMenu(null);
    setAnchorEl(null);
  }, []);

  const handleMobileOpen = useCallback(() => setMobileOpen(true), []);
  const handleMobileClose = useCallback(() => setMobileOpen(false), []);

  const drawer = (
    <Box sx={{ width: 280, pt: 2 }}>
      <List>
        {navItems.map((item) =>
          item.children ? (
            <Box key={item.label}>
              <ListItem>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              {item.children.map((child) => (
                <ListItemButton
                  key={child.href}
                  component={Link}
                  to={child.href}
                  sx={{ pl: 4 }}
                  onClick={handleMobileClose}
                >
                  <ListItemText primary={child.label} />
                </ListItemButton>
              ))}
            </Box>
          ) : (
            <ListItemButton
              key={item.href}
              component={Link}
              to={item.href!}
              onClick={handleMobileClose}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          )
        )}
        <ListItem sx={{ pt: 2, gap: 1, flexDirection: 'column' }}>
          <Button
            variant="contained"
            component={Link}
            to="/get-started"
            fullWidth
            onClick={() => {
              capture('cta_clicked', {
                location: 'header_mobile',
                cta_label: 'Sign up',
                cta_href: '/get-started',
                cta_type: 'primary',
                cta_external: false,
              });
              handleMobileClose();
            }}
          >
            Sign up
          </Button>
          <Button
            variant="outlined"
            component="a"
            href="https://dashboard.neumind.io/login"
            fullWidth
          >
            Log in
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)', // Safari support
        // Fallback for browsers that don't support backdrop-filter
        '@supports not (backdrop-filter: blur(10px))': {
          bgcolor: 'rgba(255, 255, 255, 0.98)',
        },
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src="https://assets.neumind.io/logo.png"
              alt="Neumind"
              width={173}
              height={40}
              loading="eager"
              decoding="async"
              sx={{ height: 40 }}
            />
          </Link>

          {isMobile ? (
            <>
              <IconButton
                edge="end"
                onClick={handleMobileOpen}
                aria-label="Open navigation menu"
                sx={{ color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleMobileClose}
              >
                {drawer}
              </Drawer>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {navItems.map((item) =>
                  item.children ? (
                    <Box key={item.label}>
                      <Button
                        data-dropdown-trigger
                        onClick={(e) => handleMenuToggle(e, item.label)}
                        endIcon={
                          <KeyboardArrowDownIcon
                            sx={{
                              transition: 'transform 200ms',
                              transform: openMenu === item.label ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                          />
                        }
                        sx={{
                          color: openMenu === item.label ? 'primary.main' : 'text.primary',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      key={item.href}
                      component={Link}
                      to={item.href!}
                      sx={{
                        color: 'text.primary',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  )
                )}
              </Box>

              {/* Enterprise Dropdown Popper */}
              <Popper
                open={Boolean(openMenu)}
                anchorEl={anchorEl}
                placement="bottom-start"
                transition
                sx={{ zIndex: theme.zIndex.modal }}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={200}>
                    <Box>
                      <ClickAwayListener onClickAway={handleClickAway}>
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 3,
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
                            overflow: 'hidden',
                            minWidth: 320,
                          }}
                        >
                          <Box sx={{ p: 1 }}>
                            {navItems
                              .find((item) => item.label === openMenu)
                              ?.children?.map((child) => (
                                <Box
                                  key={child.href}
                                  component={Link}
                                  to={child.href}
                                  onClick={handleMenuClose}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    transition: 'all 150ms',
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                                      '& .dropdown-icon': {
                                        color: 'primary.main',
                                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                                      },
                                    },
                                  }}
                                >
                                  <Box
                                    className="dropdown-icon"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 40,
                                      height: 40,
                                      borderRadius: 2,
                                      bgcolor: 'grey.100',
                                      color: 'grey.600',
                                      transition: 'all 150ms',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {child.icon}
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        mb: 0.25,
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      {child.label}
                                    </Typography>
                                    {child.description && (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: 'text.secondary',
                                          fontSize: '0.875rem',
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        {child.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                          </Box>
                        </Paper>
                      </ClickAwayListener>
                    </Box>
                  </Fade>
                )}
              </Popper>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  component="a"
                  href="https://dashboard.neumind.io/login"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    px: 2.5,
                    py: 1,
                  }}
                >
                  Log in
                </Button>
                <Button
                  variant="contained"
                  component={Link}
                  to="/get-started"
                  onClick={() =>
                    capture('cta_clicked', {
                      location: 'header',
                      cta_label: 'Sign up',
                      cta_href: '/get-started',
                      cta_type: 'primary',
                      cta_external: false,
                    })
                  }
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    px: 2.5,
                    py: 1,
                  }}
                >
                  Sign up
                </Button>
              </Box>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
}

// Memoize to prevent unnecessary re-renders
export const Header = memo(HeaderComponent);
